import { useEffect, useRef, useState } from "react";

import type { Scenario, ScenarioVessel, VesselType } from "@nav-games/irpcs";

interface ScenarioFigureProps {
  scenario: Scenario;
  size?: number;
  /// When the answer panel has been revealed, the renderer freezes
  /// in place + draws CPA-style track guides so the player can read
  /// the geometry. Animation runs only while `revealed` is false.
  revealed: boolean;
}

interface VesselSpriteProps {
  vessel: ScenarioVessel;
  /// Animation progress 0..1
  t: number;
  /// Duration in ms for trail rendering
  durationMs: number;
}

const SEA = "#dde7ef";
const COMPASS = "#7d8b9a";

/// Top-down chart-style scenario renderer. Two-ish vessels start at
/// their declared positions and translate along their heading
/// vectors over `scenario.durationMs`. Once `revealed === true`,
/// the animation freezes and a dotted track is drawn from start to
/// end for each vessel — that helps the player visualise CPA after
/// they've answered.
export function ScenarioFigure({
  scenario,
  size = 320,
  revealed,
}: ScenarioFigureProps) {
  const [t, setT] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startedAt = useRef<number>(performance.now());

  useEffect(() => {
    // Reset on scenario change so we don't carry a previous play's
    // animation state into a fresh card.
    setT(0);
    startedAt.current = performance.now();
  }, [scenario.id]);

  useEffect(() => {
    if (revealed) {
      // Snap to end frame when the answer panel opens.
      setT(1);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      return;
    }
    const tick = () => {
      const elapsed = performance.now() - startedAt.current;
      const nextT = Math.min(1, elapsed / scenario.durationMs);
      setT(nextT);
      if (nextT < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [scenario.id, scenario.durationMs, revealed]);

  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      style={{ display: "block", background: SEA, borderRadius: 8 }}
      aria-label={scenario.title}
    >
      {/* Compass rose top-left */}
      <g transform="translate(8 8)">
        <circle r="6" fill="#fff" stroke={COMPASS} strokeWidth="0.6" />
        <text
          y="-7"
          textAnchor="middle"
          fontSize="3.5"
          fill={COMPASS}
          fontFamily="ui-sans-serif, system-ui"
        >
          N
        </text>
        <line x1="0" y1="-5" x2="0" y2="5" stroke={COMPASS} strokeWidth="0.6" />
      </g>

      {/* Scale grid */}
      <g stroke="#cbd6df" strokeWidth="0.3" strokeDasharray="0.6 1">
        {[20, 40, 60, 80].map((g) => (
          <line key={`h${g}`} x1="0" y1={g} x2="100" y2={g} />
        ))}
        {[20, 40, 60, 80].map((g) => (
          <line key={`v${g}`} x1={g} y1="0" x2={g} y2="100" />
        ))}
      </g>

      {/* Track lines (faded while animating, solid on reveal) */}
      {scenario.vessels.map((v) => {
        const end = endPosition(v, scenario.durationMs);
        return (
          <line
            key={`trk-${v.label}`}
            x1={v.x0}
            y1={v.y0}
            x2={end.x}
            y2={end.y}
            stroke={vesselFill(v.type)}
            strokeOpacity={revealed ? 0.6 : 0.25}
            strokeWidth="0.5"
            strokeDasharray={revealed ? "" : "1 1.2"}
          />
        );
      })}

      {scenario.vessels.map((v) => (
        <VesselSprite
          key={v.label}
          vessel={v}
          t={t}
          durationMs={scenario.durationMs}
        />
      ))}
    </svg>
  );
}

function endPosition(
  v: ScenarioVessel,
  durationMs: number,
): { x: number; y: number } {
  // Heading 0° = north (up = -y). 90° = east (+x).
  const distance = v.speed * (durationMs / 1000);
  const rad = (v.heading * Math.PI) / 180;
  return {
    x: v.x0 + Math.sin(rad) * distance,
    y: v.y0 - Math.cos(rad) * distance,
  };
}

function VesselSprite({ vessel, t, durationMs }: VesselSpriteProps) {
  const end = endPosition(vessel, durationMs);
  const x = vessel.x0 + (end.x - vessel.x0) * t;
  const y = vessel.y0 + (end.y - vessel.y0) * t;
  const fill = vesselFill(vessel.type);
  const stroke = "#1c2530";

  return (
    <g transform={`translate(${x} ${y}) rotate(${vessel.heading})`}>
      {/* Hull triangle, pointing up (rotation aligns it with heading) */}
      <polygon
        points="0,-5 3,4 -3,4"
        fill={fill}
        stroke={stroke}
        strokeWidth="0.5"
      />
      {/* Mast / sail icon for sailing vessels */}
      {(vessel.type === "sailing-port-tack" ||
        vessel.type === "sailing-stbd-tack") && (
        <polygon
          points={
            vessel.type === "sailing-stbd-tack"
              ? "0,-4 2.5,2 0,2" // sail to starboard side (visual cue)
              : "0,-4 -2.5,2 0,2" // sail to port
          }
          fill="#f5f1e6"
          stroke={stroke}
          strokeWidth="0.4"
        />
      )}
      {/* Label */}
      <text
        y="-7"
        textAnchor="middle"
        fontSize="4"
        fill="#1c2530"
        fontWeight="700"
        fontFamily="ui-sans-serif, system-ui"
        transform={`rotate(${-vessel.heading})`}
      >
        {vessel.label}
      </text>
    </g>
  );
}

function vesselFill(type: VesselType): string {
  switch (type) {
    case "power":
      return "#7d8b9a";
    case "sailing-port-tack":
    case "sailing-stbd-tack":
      return "#3a6fa0";
    case "fishing":
      return "#3aa055";
    case "nuc":
      return "#c0382b";
    case "ram":
      return "#c08020";
    case "constrained":
      return "#6a4ea0";
  }
}
